<template>
  <div class="calendar-header-options">
    <div class="header-month-wrap">
      <div class="header-label-wrap">
        <div class="header-month-label">{{ currentMonthLabel }}</div>
        <div
          v-if="isDifferentYear"
          class="header-year-label"
        >
          {{ currentYearLabel }}
        </div>
      </div>
      <div class="header-month-chevrons">
        <ButtonCustom
          custom-icon-name="chevron-up"
          custom-icon-color=""
          custom-icon-size="16px"
          @click="handleChangeMonth(-1)"
        />
        <ButtonCustom
          custom-icon-name="chevron-down"
          custom-icon-color=""
          custom-icon-size="16px"
          @click="handleChangeMonth(1)"
        />
      </div>
    </div>
    <ButtonCustom
      class="header-today-button"
      :text="translate('today')"
      @click="moveToStart"
    />
  </div>
</template>

<script setup lang="ts">
import ButtonCustom from '@/components/elements/ButtonCustom.vue';
import EventBus from '@/utilities/eventBus';
import useTranslate from '@/utilities/composables/useTranslate';
import { ref, onUnmounted, computed } from 'vue';
import format from 'date-fns/format';
import isSameYear from 'date-fns/isSameYear';

const { translate } = useTranslate();

const currentDate = ref<Date>(new Date());
const currentMonthLabel = ref<string>(format(currentDate.value, 'LLLL', { weekStartsOn: 1 }));
const currentYearLabel = ref<string>(format(currentDate.value, 'yyyy', { weekStartsOn: 1 }));

const isDifferentYear = computed(() => !isSameYear(currentDate.value, Date.now()));

const moveToStart = () => {
  EventBus.$emit('calendar:moveToStart');
};

const handleChangeMonth = (value: number) => {
  EventBus.$emit('calendar:changeMonth', value);
};

const setCurrentMonthLabel = (date: Date) => {
  currentDate.value = date;
  currentMonthLabel.value = format(date, 'LLLL', { weekStartsOn: 1 });
  currentYearLabel.value = format(date, 'yyyy', { weekStartsOn: 1 });
};
EventBus.$on('calendar:currentMonthChanged', setCurrentMonthLabel);

const handleOnUnmounted = () => {
  EventBus.$off('calendar:currentMonthChanged', setCurrentMonthLabel);
};

onUnmounted(handleOnUnmounted);
</script>

<style lang="scss">
.calendar-header-options {
  @apply flex gap-4 mr-3;

  .header-today-button {
    @apply py-[1px] px-[7px];
  }

  .header-month-wrap {
    @apply flex items-center gap-2;

    .header-label-wrap {
      @apply flex items-center gap-1;
    }

    .header-month-label,
    .header-year-label {
      @apply text-sm text-text-default;
    }

    .header-month-chevrons {
      @apply flex gap-1;

      .button-wrap {
        height: unset;
        width: unset;
        @apply flex items-center justify-center cursor-pointer bg-transparent p-1 rounded;

        &:hover {
          @apply bg-icon-bg;
        }

        .custom-icons {
          margin-right: unset;
          @apply bg-icon-default w-4 h-4;

          &:hover {
            @apply bg-icon-hover;
          }
        }
      }
    }
  }
}
</style>
